import React from 'react';
import { Button, Text, tokens } from '@fluentui/react-components';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  retryKey: number;
}

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallbackMessage?: string },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode; fallbackMessage?: string }) {
    super(props);
    this.state = { hasError: false, error: null, retryKey: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            padding: 48,
            minHeight: 300,
          }}
        >
          <Text size={500} weight="semibold">
            {this.props.fallbackMessage || 'Something went wrong'}
          </Text>
          <Text
            size={300}
            style={{ color: tokens.colorPaletteRedForeground1, maxWidth: 500, textAlign: 'center' }}
          >
            {this.state.error?.message || 'An unexpected error occurred.'}
          </Text>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              appearance="primary"
              onClick={() => this.setState(prev => ({ hasError: false, error: null, retryKey: prev.retryKey + 1 }))}
            >
              Try Again
            </Button>
            <Button
              appearance="secondary"
              onClick={() => {
                const detail = `${this.state.error?.message}\n\n${this.state.error?.stack || ''}`;
                navigator.clipboard.writeText(detail).catch(() => {});
              }}
            >
              Copy Error Details
            </Button>
          </div>
        </div>
      );
    }
    // Use retryKey to force full remount on "Try Again", preventing infinite loops
    // from children with corrupted state
    return <React.Fragment key={this.state.retryKey}>{this.props.children}</React.Fragment>;
  }
}
